import React, { useMemo } from "react";
import { colors2, getVal } from "oolib";
import { UI_CAPTION } from "oolib/dist/v2/components/Typo2";

import TableElem from "@/components/cards/TableElem";
import { useElemInView } from "@/utils/customHooks/useElemInView";
import { useGetDataLazy } from "@/utils/react-query-hooks/getData";
import { WidgetEmptyState, WidgetErrorState, WidgetLoadingState } from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { ASSESSMENTS, DATASETS, VOLUNTEER_TAG_TYPE } from "../madSettings";
import { ControlsRow } from "./ControlsRow";

/**
 * "Started" — each child with at least one assessment in the template, their
 * volunteer, and a tick or cross per assessment (Baseline → Endline).
 *
 * Everything comes from the template's own published docs, one doc per
 * (child, assessment): the child is the Child Name dropdown's saved option,
 * the assessment is the Baseline/Midline/Endline radio, the volunteer is the
 * doc's volunteer tag. A row is a child; an assessment is ticked when a doc
 * for it exists. The report's filters and the widget's own go straight into
 * that one query, so they filter the ROWS.
 *
 * Lazy loading, and the catch with it: docs are paged, but a row needs ALL
 * of its child's docs. So the query is sorted by child id — a child's docs
 * arrive next to each other — and the LAST child of the last loaded page is
 * held back until the next page lands (or there are no more pages), because
 * the page boundary may have cut that child's docs in two. Every row on
 * screen is complete.
 */
const PAGE_SIZE = 100; // docs, not children — up to five docs a child

const VOLUNTEER_TAGS_PATH = `tags.${VOLUNTEER_TAG_TYPE}.data`;

const TABLE_CONFIG = {
  rowIdPath: "rowId",
  freezeFirstCol: true,
  compactHeader: true,
  colWidths: { child: 220, volunteer: 200 },
  cellData: [
    {
      colId: "child",
      headerDisplay: "Child",
      comp: "TableText",
      valuePath: "child",
      props: { typoComp: "UI_BODY_SEMIBOLD_SM", color: colors2.grey80 },
    },
    { colId: "volunteer", headerDisplay: "Volunteer", comp: "TableText", valuePath: "volunteer" },
    ...ASSESSMENTS.map((a) => ({
      colId: a.value,
      headerDisplay: a.display,
      comp: "RightWrong",
      valuePath: `status.${a.value}`,
    })),
  ],
};

/**
 * Docs (already sorted by child) → one row per child, in that order.
 * `holdBackLast`: more pages are coming, so the last child may be missing
 * docs — leave them out until the next page completes them.
 */
const rowsOf = ({ docs, dataset, holdBackLast }) => {
  const rows = [];
  let current = null;
  for (const doc of docs) {
    const childId = String(getVal(doc, dataset.childValuePath));
    if (!current || current.rowId !== childId) {
      current = {
        rowId: childId,
        child: getVal(doc, dataset.childDisplayPath) || childId,
        volunteers: [],
        status: Object.fromEntries(ASSESSMENTS.map((a) => [a.value, false])),
      };
      rows.push(current);
    }
    const assessment = getVal(doc, dataset.assessmentValuePath);
    if (assessment in current.status) current.status[assessment] = true;
    // a child who moved volunteers lists each of them once
    const volunteer = getVal(doc, VOLUNTEER_TAGS_PATH)?.[0]?.display;
    if (volunteer && !current.volunteers.includes(volunteer)) current.volunteers.push(volunteer);
  }
  if (holdBackLast) rows.pop();
  return rows.map(({ volunteers, ...row }) => ({ ...row, volunteer: volunteers.join(", ") }));
};

export const StartedChildren = ({ id, widgetIdx, contentType, filters, controls }) => {
  const dataset = DATASETS[contentType];

  const query = useGetDataLazy({
    contentTypes: [contentType],
    activeFilters: filters,
    findQuery: { kp_published_status: "published" },
    // by child, so one child's docs are contiguous across the paging
    activeSort: { [dataset.childValuePath]: 1 },
    projection: { main: 1, tags: 1 },
    limit: PAGE_SIZE,
    collectionId: `${id}:madAssessmentStatus`,
  });

  const { observerRef } = useElemInView({
    callback: query.fetchNextPage,
    enabled: !!query.hasNextPage && !query.isFetchingNextPage,
  });

  const rows = useMemo(
    () =>
      rowsOf({
        docs: (query.data?.pages || []).flatMap((page) => page.data || []),
        dataset,
        holdBackLast: !!query.hasNextPage,
      }),
    [query.data, query.hasNextPage, dataset]
  );

  useUpdateLoadedWidgetsTracker({ status: query.status, widgetIdx, hasData: rows.length > 0, widgetId: id });

  const settled = query.status === "success";

  return (
    <>
      <ControlsRow controls={controls}>
        {settled && (
          <UI_CAPTION style={{ color: colors2.grey60 }}>
            {rows.length}
            {query.hasNextPage ? "+" : ""} {rows.length === 1 && !query.hasNextPage ? "child" : "children"}
          </UI_CAPTION>
        )}
      </ControlsRow>
      {query.status === "loading" ? (
        <WidgetLoadingState />
      ) : query.status === "error" ? (
        <WidgetErrorState error={query.error} />
      ) : rows.length === 0 && !query.hasNextPage ? (
        <WidgetEmptyState display="No assessments match" />
      ) : (
        <TableElem
          id={`${id}:${contentType}:started`}
          status="success"
          data={rows}
          tableConfig={TABLE_CONFIG}
          lazyLoad={{
            observerRef: query.hasNextPage ? observerRef : null,
            isFetchingNextPage: query.isFetchingNextPage,
          }}
        />
      )}
    </>
  );
};
